<script setup>
import { ref, onErrorCaptured } from 'vue';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { captureError } from '@/lib/sentry';

const error = ref(null);
const errorInfo = ref(null);

onErrorCaptured((err, instance, info) => {
  error.value = err;
  errorInfo.value = info;

  // Report to Sentry with component context
  captureError(err, {
    component: instance?.$options?.name || 'Unknown',
    lifecycleHook: info,
    boundary: 'ErrorBoundary',
  });

  return false; // Prevent error from propagating
});

function handleRetry() {
  error.value = null;
  errorInfo.value = null;
}
</script>

<template>
  <div>
    <div v-if="error" class="rounded-md bg-yellow-50 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <ExclamationTriangleIcon class="h-5 w-5 text-yellow-400" aria-hidden="true" />
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-yellow-800">Something went wrong</h3>
          <div class="mt-2 text-sm text-yellow-700">
            <p>{{ error.message }}</p>
            <div v-if="errorInfo" class="mt-2">
              <details class="text-xs">
                <summary class="cursor-pointer hover:text-yellow-800">Technical details</summary>
                <pre class="mt-2 whitespace-pre-wrap text-yellow-600">{{ errorInfo }}</pre>
              </details>
            </div>
          </div>
          <div class="mt-4">
            <button
              type="button"
              class="inline-flex items-center rounded-md bg-yellow-50 px-2.5 py-1.5 text-sm font-semibold text-yellow-800 shadow-sm hover:bg-yellow-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
              @click="handleRetry"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <slot></slot>
    </div>
  </div>
</template>
